import { JSX, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Button, CircularProgress, Grid } from '@mui/material';
import { FormContainer, useForm, UseFormReturn } from 'react-hook-form-mui';
import { useParams, useNavigate } from 'react-router';
import { PageTitle, Spinner } from '@/components/atoms';
import { hasPermission } from '@utils/permissions';
import { useAuth } from '@contexts/auth';
import { BaseAPI } from '@/services/api';
import { ModelProps } from '@/types';
import { mapErrors } from '@/utils/errors';
import { AxiosError, AxiosResponse } from 'axios';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { usePrepareSubmit } from '@/hooks/usepreparesubmit';
import { enqueueSnackbar } from 'notistack';

type Props<T extends z.ZodTypeAny> = {
  api: BaseAPI;
  children?: React.ReactElement[] | React.ReactElement;
  modelProps: ModelProps;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  setChildValues?: (setValue: Function) => void;
  formcontext?: UseFormReturn<z.infer<T>>;
  hideBack?: boolean;
  hideSave?: boolean;
  onEditing?: (data: z.infer<T>) => void;
  schema: T;
  title: string;
  customPath?: string;
  submitApi?: BaseAPI;
  disableUpdate?: boolean;
  enableDraft?: boolean;
  handleDraft?: () => void;
  handleSubmitFunction?: () => void;
  canSaveAndContinue?: boolean;
  keepValues?: string[];
  onAfterSaveAndContinue?: () => void;
};

const UpdatePage = <T extends z.ZodTypeAny>(props: Props<T>): JSX.Element => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const prepareSubmit = usePrepareSubmit();

  const { t } = useTranslation();
  const {
    api,
    children,
    modelProps,
    setChildValues,
    submitApi,
    disableUpdate,
    enableDraft,
    handleDraft,
    handleSubmitFunction,
  } = props;
  const defaultFormContext = useForm<T>({
    resolver: zodResolver(props.schema),
  }); // Hook sempre chamado
  const formContext = props.formcontext ?? defaultFormContext;

  const queryClient = useQueryClient();
  const { reset, setError, setValue, formState } = formContext;
  const [presistPage, setPersistPage] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const { permissions } = useAuth();
  const canFetch: boolean = id !== undefined;
  const {
    data: response,
    isLoading,
    isRefetching,
  } = useQuery<AxiosResponse<T>, AxiosError>({
    queryKey: [`${modelProps.appName}.${modelProps.model}`, id],
    queryFn: async () => {
      return await api.detail<T>(Number(id));
    },
    enabled: canFetch,
  });
  const listUrl: string = props.customPath
    ? `/${props.customPath}`
    : `/${modelProps.appName}/${modelProps.model}`;

  const mutation = useMutation<
    AxiosResponse<z.infer<T>>,
    AxiosError,
    z.infer<T>
  >({
    mutationFn: async (data: T) => {
      return await (canFetch && !disableUpdate
        ? submitApi
          ? submitApi.update<T>(id as unknown as number, data)
          : api.update<T>(id as unknown as number, data)
        : submitApi
          ? submitApi.create<T>(data)
          : api.create<T>(data));
    },
    onSuccess: () => {
      if (presistPage) {
        enqueueSnackbar(`${props.title} - ${t('Saved Successfully')}`, {
          variant: 'success',
        });
        const keep = props.keepValues ?? [];
        const currentValues = formContext.getValues();
        const valuesToKeep: Record<string, unknown> = {};

        keep.forEach((field) => {
          if (field in currentValues) {
            if (field === 'order') {
              // Se for order, incrementa +1 ao invés de manter o valor atual
              const currentOrder = (currentValues as Record<string, unknown>)[
                field
              ] as number;
              valuesToKeep[field] = (currentOrder || 0) + 1;
            } else {
              valuesToKeep[field] = (currentValues as Record<string, unknown>)[
                field
              ];
            }
          }
        });

        // Lógica especial para translations - preserva language_code e inlineIndex, mas limpa name
        const currentValuesRecord = currentValues as Record<string, unknown>;
        if (
          'translations' in currentValuesRecord &&
          Array.isArray(currentValuesRecord.translations)
        ) {
          valuesToKeep.translations = (
            currentValuesRecord.translations as Array<Record<string, unknown>>
          ).map((translation) => ({
            language_code: translation.language_code,
            inlineIndex: translation.inlineIndex,
            name: '', // Limpa apenas o name
          }));
        }

        // Se não há translations, adiciona valores padrão para order e is_active se não estiverem em keepValues
        if (!('translations' in currentValuesRecord)) {
          if (!keep.includes('order')) {
            valuesToKeep.order = 0;
          }
          if (!keep.includes('is_active')) {
            valuesToKeep.is_active = true;
          }
        }

        reset(valuesToKeep as z.infer<T>);

        // Chama callback após reset (save and continue)
        if (props.onAfterSaveAndContinue) {
          props.onAfterSaveAndContinue();
        }
      } else {
        navigate(listUrl);
        queryClient.invalidateQueries({
          queryKey: [`${modelProps.appName}.${modelProps.model}`],
          refetchType: 'none',
        });
      }
    },
    onError: (error) => {
      mapErrors<z.infer<T>>({
        error,
        callbackFn: ({ key, value }) => {
          setError(key, { message: value });
        },
      });
    },
  });
  const handleBack = () => navigate(listUrl);

  const { onEditing } = props;

  useEffect(() => {
    if (!isLoading && !isSubmitting) {
      if (id && !formState.isDirty) {
        reset(response?.data);
      }
      if (onEditing) {
        onEditing(response?.data);
      }
    }
  }, [
    isLoading,
    isRefetching,
    isSubmitting,
    response?.data,
    id,
    onEditing,
    reset,
    formState.isDirty,
  ]);

  useEffect(() => {
    const perms: string = canFetch
      ? `${modelProps.appName}.change_${modelProps.model}`
      : `${modelProps.appName}.add_${modelProps.model}`;

    if (permissions != null && !hasPermission(permissions, perms)) {
      navigate('/404');
    }
  }, [permissions, canFetch, modelProps.appName, modelProps.model, navigate]);

  useEffect(() => {
    if (id === undefined && setChildValues != undefined) {
      setChildValues(setValue);
    }
  }, [id, setChildValues, setValue]);

  const handleSubmit = (data: z.infer<T>) => {
    if (mutation.isPending) return;
    setIsSubmitting(true);
    const payload = prepareSubmit(data);
    mutation.mutate(payload);
  };

  return (
    <>
      <PageTitle title={props.title} />
      <Box
        sx={{
          p: 3,
          boxShadow: 1,
          borderRadius: 2,
          bgcolor: 'white !important',
        }}
      >
        <Spinner open={(isLoading || mutation.isPending) && canFetch} />
        <FormContainer formContext={formContext} onSuccess={handleSubmit}>
          <Grid container spacing={2}>
            {children}
          </Grid>
          <Box
            display="flex"
            mt={3}
            sx={{ width: '100%', justifyContent: 'space-between' }}
          >
            {!props.hideBack && (
              <Button
                variant="contained"
                onClick={handleBack}
                color="secondary"
                startIcon={<ArrowBackIcon />}
              >
                {t('Back')}
              </Button>
            )}
            <Box display={'flex'} gap={2}>
              {enableDraft && handleDraft && (
                <Button
                  disabled={mutation.isPending}
                  variant="contained"
                  disableElevation
                  startIcon={!mutation.isPending ? <SaveIcon /> : undefined}
                  color="secondary"
                  onClick={() => {
                    handleDraft();
                    formContext.handleSubmit(handleSubmit)();
                  }}
                >
                  {!mutation.isPending ? (
                    t('Save Draft')
                  ) : (
                    <CircularProgress size={24} />
                  )}
                </Button>
              )}

              {!props.hideSave && (
                <>
                  {props.canSaveAndContinue && (
                    <Button
                      disabled={mutation.isPending}
                      variant="outlined"
                      disableElevation
                      startIcon={!mutation.isPending ? <SaveIcon /> : undefined}
                      onClick={() => {
                        setPersistPage(true);
                        if (handleSubmitFunction) {
                          handleSubmitFunction();
                        }
                        formContext.handleSubmit(handleSubmit)();
                      }}
                    >
                      {!mutation.isPending ? (
                        t('Save and Continue')
                      ) : (
                        <CircularProgress size={24} />
                      )}
                    </Button>
                  )}
                  <Button
                    disabled={mutation.isPending}
                    variant="contained"
                    disableElevation
                    startIcon={!mutation.isPending ? <SaveIcon /> : undefined}
                    onClick={() => {
                      setPersistPage(false);
                      if (handleSubmitFunction) {
                        handleSubmitFunction();
                      }
                      formContext.handleSubmit(handleSubmit)();
                    }}
                  >
                    {!mutation.isPending ? (
                      t('Save')
                    ) : (
                      <CircularProgress size={24} />
                    )}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </FormContainer>
      </Box>
    </>
  );
};

export { UpdatePage };
